import type { IGlobalTab } from '@carboncopyco/types/desktop';
import { type DependencyList, useLayoutEffect, useRef } from 'react';
import { useTabManager } from '../../services/TabManager';
import { useTabs } from '../../store/context';
import { useResponsiveModeContext } from '../../store/context/ResponsiveModeContext';

const InnerViewAreaContent = ({
  activeGlobalTabId,
  globalTabs,
  deps,
}: {
  activeGlobalTabId: number;
  globalTabs: IGlobalTab[];
  deps?: DependencyList;
}) => {
  const innerViewAreaRef = useRef<HTMLDivElement>(null);
  const { getActiveGlobalTab } = useTabManager();
  const { innerTabs } = getActiveGlobalTab();
  const activeTabInnerTabsIds = innerTabs.map((tab) => tab.id);
  const {
    isResponsiveModeActive,
    deviceSize,
    zoomFactor,
    setClippedAreaDimensions,
    responsiveModeBasedClasses,
    responsiveModeBasedStyles,
  } = useResponsiveModeContext();

  const setInnerViewAreaBounds = async () => {
    if (!innerViewAreaRef.current) return;

    const clientRect = innerViewAreaRef.current.getBoundingClientRect();
    const dimensions = {
      width: Math.round(clientRect.width),
      height: Math.round(clientRect.height),
      x: Math.round(clientRect.left),
      y: Math.round(clientRect.top),
    };

    if (isResponsiveModeActive) {
      const elementOffset = {
        x: innerViewAreaRef.current.offsetLeft,
        y: innerViewAreaRef.current.offsetTop,
      };

      if (setClippedAreaDimensions) {
        setClippedAreaDimensions({
          x: elementOffset.x,
          y: elementOffset.y,
          width: clientRect.width,
          height: clientRect.height,
        });
      }
    }

    await window.dsDesktop.layout.setInnerViewAreaBounds(
      activeTabInnerTabsIds,
      dimensions
    );
  };

  useLayoutEffect(() => {
    setInnerViewAreaBounds();
  }, [
    activeGlobalTabId,
    globalTabs,
    innerTabs.length,
    deviceSize,
    isResponsiveModeActive,
    zoomFactor,
    ...(deps || []),
  ]);

  useLayoutEffect(() => {
    window.addEventListener('resize', setInnerViewAreaBounds);

    return () => {
      window.removeEventListener('resize', setInnerViewAreaBounds);
    };
  }, [
    activeGlobalTabId,
    globalTabs,
    innerTabs.length,
    deviceSize,
    isResponsiveModeActive,
    zoomFactor,
    ...(deps || []),
  ]);

  return (
    <div className="flex flex-col min-w-0 min-h-0 flex-1">
      <div
        className={responsiveModeBasedClasses?.wrapper}
        style={responsiveModeBasedStyles?.wrapper}
      >
        <div
          className={responsiveModeBasedClasses?.inner}
          style={responsiveModeBasedStyles?.inner}
        >
          <div
            className={`${responsiveModeBasedClasses?.view}`}
            style={responsiveModeBasedStyles?.view}
            ref={innerViewAreaRef}
          />
        </div>
      </div>
    </div>
  );
};

const InnerViewArea = ({
  tab,
  deps,
}: {
  tab: IGlobalTab;
  deps?: DependencyList;
}) => {
  const { activeGlobalTabId, globalTabs } = useTabs();

  if (tab.id !== activeGlobalTabId) return null;

  return (
    <InnerViewAreaContent
      globalTabs={globalTabs}
      activeGlobalTabId={activeGlobalTabId}
      deps={deps}
    />
  );
};

export default InnerViewArea;
